import React from "react";
import { View, ScrollView, TouchableOpacity, Linking } from "react-native";
import { AppText } from "@/components/ui/AppText";
import { useI18n } from "@/context/I18nContext";
import { StepIndicator, type Step } from "@/components/ui/StepIndicator";
import { MaterialCommunityIcons } from "@expo/vector-icons";
import { Icon } from "@/components/ui/Icon";
import { useIsWideScreen } from "@/hooks/useIsWideScreen";
import { Button } from "@/components/ui/Button";
import { PageFooter } from "@/components/ui/PageFooter";
import { useRouter } from "expo-router";
import { useAuth } from "@/context/AuthContext";

const STEPS: Step[] = [
  {
    label: "vendor.pendingReview.step.createAccount",
    description: "vendor.pendingReview.step.completed",
    status: "completed",
  },
  {
    label: "vendor.pendingReview.step.submitApplication",
    description: "vendor.pendingReview.step.completed",
    status: "completed",
  },
  {
    label: "vendor.pendingReview.step.approveApplication",
    description: "vendor.pendingReview.step.pending",
    status: "pending",
  },
];

export default function PendingReviewScreen() {
  const { t } = useI18n();
  const isWide = useIsWideScreen();
  const router = useRouter();
  const { dismissPendingReview } = useAuth();

  const handleGoToDashboard = () => {
    dismissPendingReview();
    router.dismissTo("/(vendor)/(dashboard)");
  };

  return (
    <ScrollView
      className="flex-1 bg-stroke-10"
      contentContainerClassName="pt-24"
      contentContainerStyle={{ flexGrow: 1 }}
    >
      <View className="self-center w-full max-w-xl px-page-x pt-10 pb-4 items-center">
        {/* ─── Illustration ────────────────────────────────────────────── */}
        <View className="rounded-full bg-primary/10 items-center justify-center mb-6 size-20 tablet:size-[120px]">
          <AppText className="text-4xl tablet:text-6xl">🎉</AppText>
        </View>

        {/* ─── Title ───────────────────────────────────────────────────── */}
        <AppText
          className="text-dark-text-50 text-2xl tablet:text-3xl text-center mb-3"
          weight="bold"
        >
          {t("vendor.pendingReview.title")}
        </AppText>

        {/* ─── Body ────────────────────────────────────────────────────── */}
        <AppText className="text-sm text-supporting-text text-center leading-6 mb-8">
          {t("vendor.pendingReview.body")}
        </AppText>

        {/* ─── Progress steps ──────────────────────────────────────────── */}
        <View className="w-full mb-8">
          <StepIndicator steps={STEPS} orientation={isWide ? "horizontal" : "vertical"} />
        </View>

        {/* ─── Divider ─────────────────────────────────────────────────── */}
        <View className="w-full border-t border-stroke-50 mb-6" />

        {/* ─── Contact section ─────────────────────────────────────────── */}
        <AppText className="text-sm text-dark-text-50 text-center mb-4">
          {t("vendor.pendingReview.contactTitle")}
        </AppText>

        <View className="flex-row flex-wrap justify-center gap-8">
          <TouchableOpacity
            className="flex-row items-center gap-2"
            onPress={() => Linking.openURL(`mailto:${t("vendor.pendingReview.supportEmail")}`)}
            accessibilityRole="link"
          >
            <Icon
              as={MaterialCommunityIcons}
              name="email-outline"
              size={16}
              className="text-supporting-text"
            />
            <AppText className={`text-sm text-blue-50`}>
              {t("vendor.pendingReview.supportEmail")}
            </AppText>
          </TouchableOpacity>

          <TouchableOpacity
            className="flex-row items-center gap-2"
            onPress={() => Linking.openURL(`tel:${t("vendor.pendingReview.supportPhone")}`)}
            accessibilityRole="link"
          >
            <Icon
              as={MaterialCommunityIcons}
              name="phone-outline"
              size={16}
              className="text-supporting-text"
            />
            <AppText className={`text-sm text-blue-50`}>
              {t("vendor.pendingReview.supportPhone")}
            </AppText>
          </TouchableOpacity>
        </View>

        <Button className="mt-8" label="common.goToDashboard" onPress={handleGoToDashboard} />
      </View>
      <PageFooter />
    </ScrollView>
  );
}
